import { SavedQuery, SavedQueriesStore } from '@/types/savedQuery';
import type { LogisticsObjectScope } from '@/types/logisticsObject';
import { SearchLogisticsObjectsRequest } from '@/api/bol';

const STORAGE_KEY = 'bol_saved_queries';

/**
 * Get all saved queries from localStorage
 */
export const getSavedQueriesStore = (): SavedQueriesStore => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading saved queries from localStorage:', error);
    return {};
  }
};

/**
 * Save queries store to localStorage
 */
const saveSavedQueriesStore = (store: SavedQueriesStore): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.error('Error saving queries to localStorage:', error);
  }
};

/**
 * Get saved queries for a specific logistics object type
 */
export const getSavedQueries = (logisticsObjectType: LogisticsObjectScope): SavedQuery[] => {
  const store = getSavedQueriesStore();
  return store[logisticsObjectType] || [];
};

/**
 * Save a new query or replace existing one with the same name
 */
export const saveQuery = (
  name: string,
  query: unknown,
  logisticsObjectType: LogisticsObjectScope,
  isDefault: boolean = false,
): SavedQuery => {
  const store = getSavedQueriesStore();
  const queries = store[logisticsObjectType] || [];
  
  // Check if a query with the same name already exists
  const existingQueryIndex = queries.findIndex(q => q.name === name);
  
  const existingQuery = existingQueryIndex >= 0 ? queries[existingQueryIndex] : undefined;
  const shouldBeDefault = isDefault || !!existingQuery?.isDefault;

  const newQuery: SavedQuery = {
    id: existingQueryIndex >= 0 ? queries[existingQueryIndex].id : `query_${Date.now()}_${crypto.randomUUID()}`,
    name,
    query: query as SearchLogisticsObjectsRequest,
    logisticsObjectType,
    createdAt: new Date().toISOString(),
    isDefault: shouldBeDefault,
  };

  if (existingQueryIndex >= 0) {
    // Replace existing query with same name
    queries[existingQueryIndex] = newQuery;
    store[logisticsObjectType] = shouldBeDefault
      ? queries.map((savedQuery) => ({
        ...savedQuery,
        isDefault: savedQuery.id === newQuery.id,
      }))
      : queries;
  } else {
    // Add new query at the beginning (most recent first)
    const updatedQueries = [newQuery, ...queries];
    store[logisticsObjectType] = shouldBeDefault
      ? updatedQueries.map((savedQuery) => ({
        ...savedQuery,
        isDefault: savedQuery.id === newQuery.id,
      }))
      : updatedQueries;
  }
  
  saveSavedQueriesStore(store);
  
  return newQuery;
};

/**
 * Set or unset a default saved query for a logistics object type
 */
export const setSavedQueryDefault = (
  queryId: string,
  logisticsObjectType: LogisticsObjectScope,
  isDefault: boolean,
): SavedQuery[] => {
  const store = getSavedQueriesStore();
  const queries = store[logisticsObjectType] || [];

  const updatedQueries = queries.map((query) => {
    if (query.id === queryId) {
      return {
        ...query,
        isDefault,
      };
    }

    return {
      ...query,
      isDefault: isDefault ? false : query.isDefault,
    };
  });

  store[logisticsObjectType] = updatedQueries;
  saveSavedQueriesStore(store);

  return updatedQueries;
};

/**
 * Get default saved query for a logistics object type
 */
export const getDefaultSavedQuery = (
  logisticsObjectType: LogisticsObjectScope,
): SavedQuery | undefined => {
  const queries = getSavedQueries(logisticsObjectType);
  return queries.find((query) => query.isDefault);
};

/**
 * Delete a saved query
 */
export const deleteSavedQuery = (
  queryId: string,
  logisticsObjectType: LogisticsObjectScope
): void => {
  const store = getSavedQueriesStore();
  const queries = store[logisticsObjectType] || [];
  
  store[logisticsObjectType] = queries.filter(q => q.id !== queryId);
  
  saveSavedQueriesStore(store);
};

/**
 * Get a specific saved query by ID
 */
export const getSavedQueryById = (
  queryId: string,
  logisticsObjectType: LogisticsObjectScope
): SavedQuery | undefined => {
  const queries = getSavedQueries(logisticsObjectType);
  return queries.find(q => q.id === queryId);
};
